import { useMemo } from 'react'
import { ScoopSelector } from '../../../../ui'
import { useExplorer } from '../../../../../context/explorer'
import { SORTING, SORTING_OPTIONS } from '../../../../Insights/consts'
import styles from './ConfigTab.module.scss'

// Display rows options
const DISPLAY_ROWS_OPTIONS = [
    { label: 'Limit 5', value: 5 },
    { label: 'Limit 10', value: 10 },
    { label: 'Limit 50', value: 50 },
    { label: 'Limit 100', value: 100 },
    { label: 'Limit 500', value: 500 },
    { label: 'All', value: 'All' },
]

/**
 * ChartData component for configuring display rows, category axis, group by, and sorting.
 *
 * Uses ExplorerContext via useExplorer().
 * Uses setDisplayRows, setCategoryAxis, setDrillAttribute, and setSortColumns actions.
 */
export const ChartData = () => {
    // Get state and actions from context
    const {
        viewConfig,
        queryConfig,
        dataConfig,
        workspaceMetadata,
        summaryData,
        setDisplayRows,
        setCategoryAxis,
        setDrillAttribute,
        setSortColumns,
    } = useExplorer()

    // Handle display rows change
    const handleDisplayRowsChange = (e) => {
        setDisplayRows(e.target.value)
    }

    // Handle category axis change
    const handleCategoryAxisChange = (e) => {
        setCategoryAxis(e.target.value)
    }

    // Handle drill attribute (group by) change
    const handleDrillChange = (e) => {
        const value = e.target.value
        setDrillAttribute(value === 'None' ? undefined : value)
    }

    // Handle sort column change
    const handleSortColumnChange = (e) => {
        const newSortColumns = [...(queryConfig.sortColumns || [])]
        if (newSortColumns.length === 0) {
            newSortColumns.push({ column: e.target.value, order: SORTING.NAT })
        } else {
            newSortColumns[0] = { ...newSortColumns[0], column: e.target.value }
        }
        setSortColumns(newSortColumns.filter((col) => col.order !== SORTING.NAT))
    }

    // Handle sort order change
    const handleSortOrderChange = (e) => {
        const newSortColumns = [...(queryConfig.sortColumns || [])]
        if (newSortColumns.length === 0) {
            newSortColumns.push({
                column: sortColumnOptions[0]?.value || '',
                order: e.target.value,
            })
        } else {
            newSortColumns[0] = { ...newSortColumns[0], order: e.target.value }
        }
        setSortColumns(newSortColumns.filter((col) => col.order !== SORTING.NAT))
    }

    // Get available drill attributes from all selected tables
    const drillAttributes = useMemo(() => {
        if (!workspaceMetadata?.inboxes || !dataConfig.selectedItems?.length) return []

        const attributes = new Set()

        dataConfig.selectedItems.forEach((item) => {
            const inbox = workspaceMetadata.inboxes.find((i) =>
                i.tables?.some((t) => t.reportSeriesTableID === item.reportSeriesTableID)
            )
            if (inbox) {
                const table = inbox.tables.find(
                    (t) => t.reportSeriesTableID === item.reportSeriesTableID
                )
                table?.columns?.forEach((col) => {
                    if (!col.isMeasure && col.columnName !== 'Date') {
                        attributes.add(col.columnName)
                    }
                })
            }
        })

        return Array.from(attributes)
    }, [workspaceMetadata, dataConfig.selectedItems])

    // Build category axis options
    const categoryAxisOptions = useMemo(() => {
        const options = []

        // Add Time option for non-bar chart types
        if (!['bar', 'pictorialBar', 'radialBar'].includes(viewConfig.seriesType)) {
            options.push({ label: 'Time', value: 'Time' })
        }

        // Add drill attributes (excluding current drill attribute)
        drillAttributes.forEach((attr) => {
            if (attr !== dataConfig.drillAttribute) {
                options.push({ label: attr, value: attr })
            }
        })

        // Add Other option if not using worksheet
        if (!dataConfig.worksheetID) {
            options.push({ label: 'Other', value: 'Other' })
        }

        return options
    }, [viewConfig.seriesType, drillAttributes, dataConfig.drillAttribute, dataConfig.worksheetID])

    // Build group by options
    const groupByOptions = useMemo(() => {
        const options = [{ label: 'None', value: 'None' }]

        // Add drill attributes (excluding current category axis)
        drillAttributes.forEach((attr) => {
            if (attr !== dataConfig.categoryAxis) {
                options.push({ label: attr, value: attr })
            }
        })

        return options
    }, [drillAttributes, dataConfig.categoryAxis])

    // Build sort column options from metrics in summaryData
    const sortColumnOptions = useMemo(() => {
        const metrics = summaryData?.metrics || []
        return metrics
            .filter((m) => !['radial', 'gauge'].includes(m.type))
            .map((m) => ({ label: m.name, value: m.name }))
    }, [summaryData?.metrics])

    // Build sort order options
    const sortOrderOptions = useMemo(
        () => SORTING_OPTIONS.map((o) => ({ label: o.label, value: o.value })),
        []
    )

    // Determine if display rows selector should be shown
    const showDisplayRows = viewConfig.view !== 'kpi' && !dataConfig.worksheetID

    // Determine if category axis selector should be shown
    const showCategoryAxis = viewConfig.view === 'chart'

    // Determine if group by selector should be shown
    const showGroupBy =
        viewConfig.view === 'chart' &&
        !['scatter', 'pie', 'donut', 'pictorialBar', 'radialBar'].includes(viewConfig.seriesType)

    // Determine if sorting selectors should be shown
    const showSorting =
        viewConfig.view === 'chart' &&
        dataConfig.categoryAxis !== 'Time' &&
        !['radial', 'gauge'].includes(viewConfig.seriesType) &&
        sortColumnOptions.length > 0

    return (
        <div className={styles.sectionContainer}>
            {showDisplayRows && (
                <ScoopSelector
                    value={queryConfig.displayRows}
                    onChange={handleDisplayRowsChange}
                    label="Display"
                    options={DISPLAY_ROWS_OPTIONS}
                />
            )}

            {showCategoryAxis && (
                <ScoopSelector
                    value={dataConfig.categoryAxis}
                    onChange={handleCategoryAxisChange}
                    label="Category Axis"
                    options={categoryAxisOptions}
                />
            )}

            {showGroupBy && (
                <ScoopSelector
                    value={dataConfig.drillAttribute || 'None'}
                    onChange={handleDrillChange}
                    label="Group By"
                    options={groupByOptions}
                />
            )}

            {/* Sorting controls - shown for chart view when category axis is not Time */}
            {showSorting && (
                <>
                    <ScoopSelector
                        value={queryConfig.sortColumns?.[0]?.column || sortColumnOptions[0]?.value}
                        onChange={handleSortColumnChange}
                        label="Sort By"
                        options={sortColumnOptions}
                    />
                    <ScoopSelector
                        value={queryConfig.sortColumns?.[0]?.order || SORTING.NAT}
                        onChange={handleSortOrderChange}
                        label="Sort Order"
                        options={sortOrderOptions}
                    />
                </>
            )}
        </div>
    )
}
